/**
 * Plan Feature Keys
 *
 * These string constants are stored in the `features` array on each plan document.
 * Use these throughout the app to enable/disable functionality based on the org's plan.
 *
 * Usage:
 *   import { PLAN_FEATURES } from '@/lib/planFeatures'
 *   const canManualTrigger = features.includes(PLAN_FEATURES.MANUAL_TRIGGER)
 */
export const PLAN_FEATURES = {
  /** Core platform vitals and analytics */
  VITALS: 'vitals',
  /** User can manually trigger an analysis outside the schedule */
  MANUAL_TRIGGER: 'manual_trigger',
  /** Shopping vitals page */
  SHOPPING_VITALS: 'shopping_vitals',
  /** Org can run analyses across multiple geographic regions */
  MULTI_REGION: 'multi_region',
  /** Priority support queue */
  PRIORITY_SUPPORT: 'priority_support',
  /** Competitor tracking */
  COMPETITOR_TRACKING: 'competitor_tracking',
  /** Sentiment analysis on brand mentions */
  SENTIMENT_ANALYSIS: 'sentiment_analysis',
  /** Average position / positional tracking in LLM responses */
  POSITIONAL_TRACKING: 'positional_tracking',
  /** Automatically discover new competitor brands from LLM responses */
  AUTOMATIC_BRAND_DISCOVERY: 'automatic_brand_discovery',
  /** Access to the Vitals page with detailed feature scores */
  VITALS_PAGE: 'vitals_page',
  /** Ability to generate AI-powered fixes for vitals issues */
  VITALS_FIX_GENERATION: 'vitals_fix_generation',
  /** Full citation analysis (timeline, website breakdown, unlimited citations) */
  CITATION_ANALYSIS: 'citation_analysis',
  /** Access to Traffic and Integration Hub pages */
  INTEGRATIONS: 'integrations',
  /** Weekly report emails with brand visibility summary */
  WEEKLY_REPORTS: 'weekly_reports',
  /** Publisher persona: domain claim, aggregate counts, top-1 topic teaser */
  PUBLISHER_FREE: 'publisher_free',
  /** Publisher persona: full global topic breakdowns, co-cited leaderboards, trends */
  PUBLISHER_TOPIC_INSIGHTS: 'publisher_topic_insights',
  /** Reddit Agent and future AI agents (operator-enabled; not upsellable via upgrade modal) */
  AGENTS: 'agents',
  /** One-click 30-day PDF brand visibility report, emailed to the requesting user */
  PDF_REPORT_EXPORT: 'pdf_report_export',
  /** Keyword Intelligence: keyword extraction, GSC token-containment matching, Keywords view (operator-enabled; not upsellable via upgrade modal) */
  KEYWORD_INTELLIGENCE: 'keyword_intelligence',
  /** In-app chat assistant powered by Claude + 42a data tools (operator-enabled; off by default) */
  ASSISTANT: 'assistant',
} as const

export type PlanFeatureKey = typeof PLAN_FEATURES[keyof typeof PLAN_FEATURES]

/**
 * Top-level product modules. A plan's enabled modules determine which parts of
 * the product the sidebar renders. Features belong to at most one module;
 * `null` means the feature applies to every module (e.g. priority support).
 */
export const MODULE = {
  ANALYSIS: 'analysis',
  BRAND_VISIBILITY: 'brand_visibility',
  PUBLISHER_INSIGHTS: 'publisher_insights',
  ANALYTICS: 'analytics',
  AGENTS: 'agents',
  KEYWORD_INTELLIGENCE: 'keyword_intelligence',
  ASSISTANT: 'assistant',
} as const

export type ModuleKey = typeof MODULE[keyof typeof MODULE]

export const ALL_MODULES: ModuleKey[] = [
  MODULE.ANALYSIS,
  MODULE.BRAND_VISIBILITY,
  MODULE.PUBLISHER_INSIGHTS,
  MODULE.ANALYTICS,
  MODULE.AGENTS,
  MODULE.KEYWORD_INTELLIGENCE,
  MODULE.ASSISTANT,
]

export const MODULE_LABELS: Record<ModuleKey, string> = {
  analysis: 'Projects',
  brand_visibility: 'Brand Visibility',
  publisher_insights: 'Domain Impact',
  analytics: 'Connectivity',
  agents: 'Agents',
  keyword_intelligence: 'Keyword Intelligence',
  assistant: 'Assistant',
}

/** Where the sidebar lands when a user switches into a module. */
export const MODULE_DEFAULT_PATH: Record<ModuleKey, string> = {
  analysis: '/analysis',
  brand_visibility: '/dashboard',
  publisher_insights: '/publisher',
  analytics: '/integration-hub',
  agents: '/agents',
  keyword_intelligence: '/analysis',
  assistant: '/dashboard',
}

/** Maps each plan-feature key to the module it unlocks (or null if module-agnostic). */
export const PLAN_FEATURE_MODULES: Record<PlanFeatureKey, ModuleKey | null> = {
  vitals: MODULE.BRAND_VISIBILITY,
  manual_trigger: MODULE.BRAND_VISIBILITY,
  shopping_vitals: MODULE.BRAND_VISIBILITY,
  multi_region: MODULE.BRAND_VISIBILITY,
  priority_support: null,
  competitor_tracking: MODULE.BRAND_VISIBILITY,
  sentiment_analysis: MODULE.BRAND_VISIBILITY,
  positional_tracking: MODULE.BRAND_VISIBILITY,
  automatic_brand_discovery: MODULE.BRAND_VISIBILITY,
  vitals_page: MODULE.BRAND_VISIBILITY,
  vitals_fix_generation: MODULE.BRAND_VISIBILITY,
  citation_analysis: MODULE.BRAND_VISIBILITY,
  integrations: MODULE.ANALYTICS,
  weekly_reports: MODULE.BRAND_VISIBILITY,
  publisher_free: MODULE.PUBLISHER_INSIGHTS,
  publisher_topic_insights: MODULE.PUBLISHER_INSIGHTS,
  // Agents is operator-enabled (not a plan upgrade path). The sidebar group
  // is fully hidden for non-entitled users; do NOT use lockedFeature on items.
  agents: MODULE.AGENTS,
  pdf_report_export: MODULE.BRAND_VISIBILITY,
  // Keyword Intelligence is operator-enabled (not a plan upgrade path). The
  // Keywords view is fully hidden until the gate is flipped on.
  keyword_intelligence: MODULE.KEYWORD_INTELLIGENCE,
  // Assistant is operator-enabled like agents; off by default.
  assistant: MODULE.ASSISTANT,
}

/** Union of modules touched by the given feature keys. */
export function modulesForFeatures(keys: readonly string[]): ModuleKey[] {
  const set = new Set<ModuleKey>()
  for (const key of keys) {
    const mod = PLAN_FEATURE_MODULES[key as PlanFeatureKey]
    if (mod) set.add(mod)
  }
  return ALL_MODULES.filter((m) => set.has(m))
}

/** All feature keys belonging to a module. */
export function featuresForModule(module: ModuleKey): PlanFeatureKey[] {
  return (Object.entries(PLAN_FEATURE_MODULES) as [PlanFeatureKey, ModuleKey | null][])
    .filter(([, m]) => m === module)
    .map(([k]) => k)
}

/** Human-readable label for each feature key, used in the admin UI */
export const PLAN_FEATURE_LABELS: Record<PlanFeatureKey, string> = {
  vitals: 'Vitals & Analytics',
  manual_trigger: 'Manual Trigger',
  shopping_vitals: 'Shopping Vitals',
  multi_region: 'Multi-Region',
  priority_support: 'Priority Support',
  competitor_tracking: 'Competitor Tracking',
  sentiment_analysis: 'Sentiment Analysis',
  positional_tracking: 'Positional Tracking',
  automatic_brand_discovery: 'Automatic Brand Discovery',
  vitals_page: 'Vitals Page',
  vitals_fix_generation: 'Vitals Fix Generation',
  citation_analysis: 'Citation Analysis',
  integrations: 'Integrations',
  weekly_reports: 'Weekly Reports',
  publisher_free: 'Publisher Free Tier',
  publisher_topic_insights: 'Publisher Topic Insights',
  agents: 'Agents (Reddit Agent)',
  pdf_report_export: 'PDF Report Export',
  keyword_intelligence: 'Keyword Intelligence',
  assistant: 'In-App Chat Assistant',
}

/** All feature keys in display order */
export const ALL_PLAN_FEATURES = Object.values(PLAN_FEATURES) as PlanFeatureKey[]

/**
 * Derive marketing feature strings from plan inputs and enabled features.
 * These are shown on plan cards (billing page, onboarding modal).
 *
 * Returns strings like "Up to 10 Manual Triggers/Month", "Daily Analysis", etc.
 */
export function derivePlanMarketingFeatures(plan: {
  monthlyManualTriggerLimit?: number
  promptsPerAnalysis?: number
  numberOfAnalyses?: number
  numberOfRegions?: number
  numberOfBrandsToTrack?: number
  numberOfWebsites?: number
  monthlyRunLimit?: number
  features?: string[]
}): string[] {
  const derived: string[] = []
  const featureSet = new Set(plan.features ?? [])

  if (plan.promptsPerAnalysis != null) {
    derived.push(`${plan.promptsPerAnalysis} Prompts per Analysis`)
  }

  if (plan.numberOfAnalyses != null) {
    derived.push(`${plan.numberOfAnalyses} Analyses per Day`)
  }

  if (featureSet.has(PLAN_FEATURES.MANUAL_TRIGGER) && plan.monthlyManualTriggerLimit != null) {
    derived.push(`Up to ${plan.monthlyManualTriggerLimit} Manual Triggers/Month`)
  }

  if (featureSet.has(PLAN_FEATURES.MULTI_REGION) && plan.numberOfRegions != null && plan.numberOfRegions > 1) {
    derived.push(`${plan.numberOfRegions} Regions`)
  }

  if (featureSet.has(PLAN_FEATURES.COMPETITOR_TRACKING)) {
    derived.push('Competitor Tracking')
  }

  if (featureSet.has(PLAN_FEATURES.PRIORITY_SUPPORT)) {
    derived.push('Priority Support')
  }

  if (plan.numberOfBrandsToTrack != null) {
    derived.push(`Up to ${plan.numberOfBrandsToTrack} Tracked Brands`)
  }

  if (plan.numberOfWebsites != null) {
    derived.push(`Up to ${plan.numberOfWebsites} Tracked Websites`)
  }

  if (plan.monthlyRunLimit != null && plan.monthlyRunLimit > 0) {
    derived.push(`${plan.monthlyRunLimit.toLocaleString()} runs / month`)
  }

  if (featureSet.has(PLAN_FEATURES.SENTIMENT_ANALYSIS)) {
    derived.push('Sentiment Analysis')
  }

  if (featureSet.has(PLAN_FEATURES.POSITIONAL_TRACKING)) {
    derived.push('Positional Tracking')
  }

  if (featureSet.has(PLAN_FEATURES.CITATION_ANALYSIS)) {
    derived.push('Full Citation Analysis')
  }

  if (featureSet.has(PLAN_FEATURES.VITALS_PAGE)) {
    derived.push('Brand Vitals Page')
  }

  if (featureSet.has(PLAN_FEATURES.VITALS_FIX_GENERATION)) {
    derived.push('AI Fix Generation')
  }

  if (featureSet.has(PLAN_FEATURES.AUTOMATIC_BRAND_DISCOVERY)) {
    derived.push('Automatic Brand Discovery')
  }

  if (featureSet.has(PLAN_FEATURES.WEEKLY_REPORTS)) {
    derived.push('Weekly Reports')
  }

  if (featureSet.has(PLAN_FEATURES.PUBLISHER_FREE)) {
    derived.push('Publisher Domain Claim')
  }

  if (featureSet.has(PLAN_FEATURES.PUBLISHER_TOPIC_INSIGHTS)) {
    derived.push('Publisher Topic Insights')
  }

  return derived
}
